import { useState } from "react";
import { upvcSpecs, aluminiumSpecs } from "../../../mocks/windows";

export default function SpecsComparison() {
  const [active, setActive] = useState<"upvc" | "aluminium" | "both">("both");

  return (
    <div>
      {/* Tab switcher */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex bg-stone-100 rounded-full p-1">
          {(["both", "upvc", "aluminium"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActive(tab)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                active === tab
                  ? "bg-amber-600 text-white"
                  : "text-zinc-600 hover:text-zinc-800"
              }`}
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              {tab === "both" ? "Side by Side" : tab === "upvc" ? "uPVC Only" : "Aluminium Only"}
            </button>
          ))}
        </div>
      </div>

      {/* Side-by-side headers */}
      {active === "both" && (
        <div className="grid grid-cols-2 gap-4 mb-0">
          <div className="bg-zinc-800 rounded-t-lg px-6 py-4 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-md">
              <i className="ri-door-open-line text-white text-sm"></i>
            </div>
            <div>
              <p className="text-white font-bold text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>uPVC Windows</p>
              <p className="text-stone-400 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>Value &amp; performance</p>
            </div>
          </div>
          <div className="bg-amber-600 rounded-t-lg px-6 py-4 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-md">
              <i className="ri-door-open-line text-white text-sm"></i>
            </div>
            <div>
              <p className="text-white font-bold text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>Aluminium Windows</p>
              <p className="text-amber-100 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>Slim &amp; contemporary</p>
            </div>
          </div>
        </div>
      )}

      {active !== "both" && (
        <div className={`rounded-t-lg px-6 py-4 flex items-center gap-3 ${active === "upvc" ? "bg-zinc-800" : "bg-amber-600"}`}>
          <div className={`w-8 h-8 flex items-center justify-center rounded-md ${active === "upvc" ? "bg-white/10" : "bg-white/20"}`}>
            <i className="ri-door-open-line text-white text-sm"></i>
          </div>
          <div>
            <p className="text-white font-bold text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>
              {active === "upvc" ? "uPVC Windows — Technical Specification" : "Aluminium Windows — Technical Specification"}
            </p>
          </div>
        </div>
      )}

      {/* Specs rows */}
      {active === "both" && (
        <div className="grid grid-cols-2 gap-4 border-l border-r border-b border-stone-200 rounded-b-lg overflow-hidden">
          <div>
            {upvcSpecs.map((spec, i) => (
              <div key={spec.label} className={`px-6 py-3.5 flex justify-between items-center ${i % 2 === 0 ? "bg-stone-50" : "bg-white"}`}>
                <span className="text-zinc-500 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>{spec.label}</span>
                <span className="text-zinc-800 text-xs font-semibold text-right max-w-[55%]" style={{ fontFamily: "'Raleway', sans-serif" }}>{spec.value}</span>
              </div>
            ))}
          </div>
          <div>
            {aluminiumSpecs.map((spec, i) => (
              <div key={spec.label} className={`px-6 py-3.5 flex justify-between items-center ${i % 2 === 0 ? "bg-stone-50" : "bg-white"}`}>
                <span className="text-zinc-500 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>{spec.label}</span>
                <span className="text-zinc-800 text-xs font-semibold text-right max-w-[55%]" style={{ fontFamily: "'Raleway', sans-serif" }}>{spec.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {active === "upvc" && (
        <div className="border border-t-0 border-stone-200 rounded-b-lg overflow-hidden">
          {upvcSpecs.map((spec, i) => (
            <div key={spec.label} className={`px-6 py-3.5 flex justify-between items-center ${i % 2 === 0 ? "bg-stone-50" : "bg-white"}`}>
              <span className="text-zinc-500 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>{spec.label}</span>
              <span className="text-zinc-800 text-sm font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>{spec.value}</span>
            </div>
          ))}
        </div>
      )}

      {active === "aluminium" && (
        <div className="border border-t-0 border-stone-200 rounded-b-lg overflow-hidden">
          {aluminiumSpecs.map((spec, i) => (
            <div key={spec.label} className={`px-6 py-3.5 flex justify-between items-center ${i % 2 === 0 ? "bg-stone-50" : "bg-white"}`}>
              <span className="text-zinc-500 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>{spec.label}</span>
              <span className="text-zinc-800 text-sm font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>{spec.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Bottom explainer */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-zinc-50 rounded-lg p-5 border border-zinc-100">
          <h4 className="font-bold text-zinc-800 text-sm mb-2" style={{ fontFamily: "'Raleway', sans-serif" }}>
            <a href="#upvc">Choose uPVC if…</a>
          </h4>
          <ul className="space-y-1.5">
            {["You want the best value for money", "You prefer traditional white or woodgrain finishes", "You're replacing windows in a period property", "You want a 10-year guarantee at the lowest cost"].map(item => (
              <li key={item} className="flex items-start gap-2">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5"><i className="ri-arrow-right-s-line text-amber-500 text-sm"></i></div>
                <span className="text-zinc-500 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-amber-50 rounded-lg p-5 border border-amber-100">
          <h4 className="font-bold text-zinc-800 text-sm mb-2" style={{ fontFamily: "'Raleway', sans-serif" }}>
            <a href="#aluminium">Choose Aluminium if…</a>
          </h4>
          <ul className="space-y-1.5">
            {["You want slim sightlines and maximum glass", "You want a custom RAL powder-coat colour", "You're building or renovating a contemporary home", "You want the highest long-term durability"].map(item => (
              <li key={item} className="flex items-start gap-2">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5"><i className="ri-arrow-right-s-line text-amber-600 text-sm"></i></div>
                <span className="text-zinc-600 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
